import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import produce from 'immer';
import { inject, Observer, observer } from 'mobx-react';
import * as React from 'react';
import {
	DragDropContext,
	Draggable,
	DraggableProvided,
	DraggingStyle,
	Droppable,
	DroppableProvided,
	DropResult,
	NotDraggingStyle,
} from 'react-beautiful-dnd';
import { IFileAttachment } from '../../../../extViewmodels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { getFileSizeStringValue } from '../../../../models/UiUtils';
import { AIImageDialog } from '../../../components-v2/ai/AIImageDialog';
import { AiButton } from '../../../components-v2/AiButton';
import { Button } from '../../../components-v2/Button';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { CameraIcon } from '../../svgs/icons/CameraIcon';
import { DragGripperIcon } from '../../svgs/icons/DragGripperIcon';
import { EnlargeIcon } from '../../svgs/icons/EnlargeIcon';
import { SparklesIcon } from '../../svgs/icons/SparklesIcon';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	className?: string;
	onClickEnlargeImage?(initialImageUrl: string): void;
	onClickMedia?(): void;
	hasVideo: boolean;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

const MAX_IMAGES = 10;

const SocialMediaImageSearchAndDisplayBase: React.FC<IProps> = observer(props => {
	const { className, readOnly, hasVideo, onClickEnlargeImage, impersonationContext } = props;
	const { postImages, setImages, postContent } = useEditSocialMediaPostContext();
	const [generateImageDialogOpen, setGenerateImageDialogOpen] = React.useState(false);
	const [unifiedDialogOpen, setUnifiedDialogOpen] = React.useState(false);
	const [unifiedDialogTab, setUnifiedDialogTab] = React.useState<
		'upload' | 'ai-generate' | 'ai-edit' | 'free-images' | 'bulk-rebranding'
	>('upload');
	const [unifiedDialogImage, setUnifiedDialogImage] = React.useState<IFileAttachment | null>(null);
	// Bidirectional mapping to support multiple images per conversation
	// imageToConversation: Maps image ID to its conversation ID
	// conversationToImages: Maps conversation ID to all image IDs that were generated in that conversation
	// This allows us to track all images generated in a single conversation chain
	const [_imageToConversationMap, setImageToConversationMap] = React.useState<Record<string, string>>({});
	// Store conversation ID from generation dialog to associate with the generated image
	const [pendingGenerateConversationId, setPendingGenerateConversationId] = React.useState<string | null>(null);

	const onRemoveClicked = React.useCallback(
		(image: IFileAttachment) => () => {
			setImages(val => val?.filter(x => x.id !== image.id));
		},
		[setImages]
	);

	const _handleOpenUpload = React.useCallback(() => {
		setUnifiedDialogTab('upload');
		setUnifiedDialogImage(null);
		setUnifiedDialogOpen(true);
	}, []);

	const handleOpenBulkRebranding = React.useCallback(() => {
		setUnifiedDialogTab('bulk-rebranding');
		setUnifiedDialogImage(null);
		setUnifiedDialogOpen(true);
	}, []);

	const handleOpenAIGenerate = React.useCallback(() => {
		setUnifiedDialogTab('ai-generate');
		setUnifiedDialogImage(null);
		setUnifiedDialogOpen(true);
	}, []);

	const handleOpenFreeImages = React.useCallback(() => {
		setUnifiedDialogTab('free-images');
		setUnifiedDialogImage(null);
		setUnifiedDialogOpen(true);
	}, []);

	const handleOpenAIEdit = React.useCallback((image: IFileAttachment) => {
		setUnifiedDialogTab('ai-edit');
		setUnifiedDialogImage(image);
		setUnifiedDialogOpen(true);
	}, []);

	const handleUnifiedImageSelected = React.useCallback(
		(image: IFileAttachment) => {
			setImages(prev => {
				// If we're editing an existing image, replace it instead of adding a new one
				if (unifiedDialogImage) {
					return (prev || []).map(img => (img.id === unifiedDialogImage.id ? image : img));
				}
				// Otherwise, add the new image
				return [...(prev || []), image];
			});
		},
		[setImages, unifiedDialogImage]
	);

	const handleBulkRebrandingImagesSelected = (attachments: Api.IFileAttachment[]) => {
		setUnifiedDialogOpen(false);
		setImages(attachments);
	};

	const onDragEnd = (result: DropResult) => {
		// do nothing if the user dropped the item outside the list
		if (result.destination == null) {
			return;
		}
		const toIndex = result.destination.index;
		const fromIndex = result.source.index;
		const imageToMove = postImages[fromIndex];
		setImages(
			produce(postImages, draftImages => {
				draftImages.splice(fromIndex, 1);
				draftImages.splice(toIndex, 0, imageToMove);
			})
		);
	};

	const hasMaximumAllowedIssues = postImages?.length > MAX_IMAGES;

	const onRenderContent = (image: IFileAttachment) => {
		if (image.mimeType?.startsWith('video/')) {
			return (
				<div className={className ? className : css(styleSheet.imageDisplayArea)}>
					<figure className={css(styleSheet.imageDisplayAreaImageContainer)}>
						<video src={image.url} width={200} controls />
						<figcaption className={css(styleSheet.imageDisplayAreaInfo)}>
							<div>
								<span>File size:&nbsp;</span>
								{getFileSizeStringValue(image.fileSize)}
							</div>
						</figcaption>
					</figure>

					{/* This is where to insert other info about the content */}
					{!readOnly ? (
						<DeprecatedCloseButton className={css(styleSheet.imageDisplayAreaClose)} onClick={onRemoveClicked(image)} />
					) : null}
				</div>
			);
		}
		return (
			<div className={className ? className : css(styleSheet.imageDisplayArea)}>
				<div className={css(styleSheet.imageDisplayAreaImageContainer)}>
					<button onClick={() => onClickEnlargeImage(image.url)}>
						<img src={image.url} width={200} />
					</button>
					<div className={css(styleSheet.imageDisplayAreaInfo)}>
						<div>
							<span>File size:&nbsp;</span>
							{getFileSizeStringValue(image.fileSize)}
						</div>
						<div className={css(styleSheet.imageActions)}>
							<button onClick={() => onClickEnlargeImage(image.url)} className={css(styleSheet.enlargeImage)}>
								<EnlargeIcon />
								Enlarge Image{postImages?.length > 1 ? 's' : ''}
							</button>
							{!readOnly && !image.mimeType?.toLowerCase().includes('gif') && (
								<div>
									<Button size='sm' variant='secondary' onClick={() => handleOpenAIEdit(image)}>
										<SparklesIcon />
										Edit Image with AI
									</Button>
									<div className={css(styleSheet.sourceMention)}>Change background, colors, and more</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* This is where to insert other info about the content */}
				{!readOnly ? (
					<DeprecatedCloseButton className={css(styleSheet.imageDisplayAreaClose)} onClick={onRemoveClicked(image)} />
				) : null}
			</div>
		);
	};

	const getItemStyle = (draggableStyles: DraggingStyle | NotDraggingStyle) => {
		return { background: '#fff', ...draggableStyles };
	};
	return (
		<>
			{!readOnly && !hasVideo ? (
				<>
					<div className={css(styleSheet.generateButtonContainer)}>
						<Button size='sm' variant='secondary' onClick={handleOpenBulkRebranding}>
							<SparklesIcon />
							Apply my Branding
						</Button>

						<AiButton
							title='New! Generate Image with AI'
							onClick={handleOpenAIGenerate}
							disabled={hasMaximumAllowedIssues}
							size='default'
						/>
					</div>
					<div className={css(styleSheet.addMediaContainer, styleSheet.contentContainers)}>
						<div>
							<button
								className={css(styleSheet.iconButtons)}
								disabled={hasMaximumAllowedIssues}
								onClick={handleOpenFreeImages}
								title={hasMaximumAllowedIssues ? `Only ${MAX_IMAGES} images are supported at this time` : undefined}
							>
								<CameraIcon />
								<div className={css(styleSheet.freeImage)}> Free Media</div>
							</button>
							<div className={css(styleSheet.sourceMention)}>Royalty free media library</div>
						</div>
						<div>
							<button
								className={css(styleSheet.ownImage)}
								disabled={hasMaximumAllowedIssues}
								onClick={props.onClickMedia}
								title={hasMaximumAllowedIssues ? 'Only one media is supported at this time' : undefined}
							>
								{'+ Upload Media '}
							</button>
							<div className={css(styleSheet.sourceMention)}>Choose file from your computer</div>
						</div>
					</div>
				</>
			) : null}
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable direction='vertical' droppableId='social-post' type='image'>
					{(droppableProvided: DroppableProvided) => {
						return (
							<Observer>
								{() => {
									return (
										<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
											{postImages ? (
												<div className={css(styleSheet.imagesContainer)}>
													{postImages.map((image, idx) => {
														return !readOnly ? (
															<Draggable
																disableInteractiveElementBlocking={true}
																draggableId={image.id}
																index={idx}
																key={image.id}
															>
																{(draggableProvided: DraggableProvided) => {
																	return (
																		<div
																			className={css(styleSheet.imageContainer)}
																			key={image.id}
																			ref={draggableProvided ? draggableProvided.innerRef : undefined}
																			{...(draggableProvided?.draggableProps || {})}
																			style={getItemStyle(draggableProvided.draggableProps?.style)}
																		>
																			<div className={css(styleSheet.gripperCol)}>
																				<span {...(draggableProvided?.dragHandleProps || {})}>
																					<DragGripperIcon />
																				</span>
																			</div>
																			{onRenderContent(image)}
																		</div>
																	);
																}}
															</Draggable>
														) : (
															<div className={css(styleSheet.imageContainer)} key={image.id}>
																{onRenderContent(image)}
															</div>
														);
													})}
												</div>
											) : null}
										</div>
									);
								}}
							</Observer>
						);
					}}
				</Droppable>
			</DragDropContext>

			<AIImageDialog
				open={generateImageDialogOpen}
				onOpenChange={setGenerateImageDialogOpen}
				impersonationContext={impersonationContext}
				setImages={updater => {
					setImages(prevImages => {
						const newImages = typeof updater === 'function' ? updater(prevImages) : updater;

						// Find the newly added image(s)
						const addedImages = newImages?.filter(img => !prevImages?.some(prev => prev.id === img.id)) || [];

						// Associate the pending conversation with the newly generated image
						if (pendingGenerateConversationId && addedImages.length > 0) {
							setImageToConversationMap(prev => ({
								...prev,
								[addedImages[0].id]: pendingGenerateConversationId,
							}));

							setPendingGenerateConversationId(null); // Clear the pending conversation
						}

						return newImages;
					});
				}}
				onConversationCreated={conversationId => {
					setPendingGenerateConversationId(conversationId);
				}}
			/>

			<UnifiedImageDialog
				open={unifiedDialogOpen}
				onOpenChange={setUnifiedDialogOpen}
				initialTab={unifiedDialogTab}
				initialImage={unifiedDialogImage || undefined}
				onImageSelected={handleUnifiedImageSelected}
				impersonationContext={impersonationContext}
				postContent={postContent}
				showUploadTab={true}
				bulkRebrandingImages={postImages}
				onBulkRebrandingImagesSelected={handleBulkRebrandingImagesSelected}
			/>
		</>
	);
});

export const SocialMediaImageSearchAndDisplay = inject(ImpersonationContextKey)(SocialMediaImageSearchAndDisplayBase);
